import { useCallback, useEffect, useRef, useState } from 'react';
import { isUnauthorized, lmError, lmErrorCode } from './lmApi';
import { isStepUpRequired } from '../../lib/stepUp';

/**
 * Server-driven, bounded row fetch for one List Management view.
 * Debounced (300 ms) so typing in the search box does not fire a request per
 * keystroke; an AbortController drops stale responses.
 *
 * `batchContext` (optional) names the import population the view shows. While it
 * stays the same, `batches` is the UNION of every page's batches seen so far, so
 * the columns the imports carry do not shrink from page to page (#974). A new
 * context, or a refetch, starts again from the page just loaded.
 */
export default function useLmRows(api, params, { enabled = true, debounceMs = 300, batchContext = null } = {}) {
    const [state, setState] = useState({ data: [], total: 0, selectableTotal: 0, batches: {}, loading: true, error: null, status: 'loading' });
    const [tick, setTick] = useState(0);
    const key = JSON.stringify(params);
    // Asking for different file columns (#974) refetches the same view; only a
    // change to the view itself empties the table while it loads.
    const { source_columns: _sourceColumns, ...viewParams } = params;
    const viewKey = JSON.stringify(viewParams);
    const abortRef = useRef(null);
    const keyRef = useRef(viewKey);

    const refetch = useCallback(() => setTick((t) => t + 1), []);

    useEffect(() => {
        if (!enabled) return undefined;
        // A new view/page must not show (or let anyone select) the previous view's
        // rows while it loads; a plain refetch keeps them to avoid a flash.
        const changed = keyRef.current !== viewKey;
        keyRef.current = viewKey;
        setState((s) => (changed ? { ...s, data: [], total: 0, selectableTotal: 0, loading: true, status: 'loading' }
            : { ...s, loading: true }));
        // Batches accumulate only within one population and one refetch round.
        const stamp = batchContext == null ? null : `${batchContext}#${tick}`;
        const timer = setTimeout(() => {
            abortRef.current?.abort?.();
            const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
            abortRef.current = ctrl;
            api.rows(JSON.parse(key), ctrl?.signal)
                .then((res) => {
                    if (ctrl?.signal?.aborted) return;
                    setState((s) => ({
                        data: res.data || [], total: res.total || 0,
                        batches: stamp !== null && s.batchesStamp === stamp ? { ...s.batches, ...(res.batches || {}) } : (res.batches || {}),
                        batchesStamp: stamp,
                        selectableTotal: res.selectable_total ?? res.total ?? 0,
                        loading: false, error: null, status: 'ok', pageSize: res.page_size,
                    }));
                })
                .catch((err) => {
                    if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                    const status = isStepUpRequired(err) ? 'step_up'
                        : isUnauthorized(err) ? 'unauthorized'
                        : lmErrorCode(err) === 'feature_not_available' ? 'unavailable' : 'error';
                    setState((s) => ({ ...s, loading: false, error: lmError(err, 'Could not load records.'), status }));
                });
        }, debounceMs);
        return () => {
            clearTimeout(timer);
            abortRef.current?.abort?.();  // a response for the old params must never land as current
        };
    }, [api, key, viewKey, tick, enabled, debounceMs, batchContext]);

    useEffect(() => () => abortRef.current?.abort?.(), []);

    return { ...state, refetch };
}
